import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function useSubscription() {
  const { userProfile } = useAuth();
  const [isActive, setIsActive] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  useEffect(() => {
    if (userProfile?.subscription) {
      const sub = userProfile.subscription;
      const now = new Date();

      // Derive trial expiry client-side so we don't depend solely on backend status updates
      const trialExpired = sub.is_trial && !!sub.trial_end && new Date(sub.trial_end) < now;

      // Active = backend says active/trial AND the trial hasn't actually expired yet
      const active = ['trial', 'active'].includes(sub.status) && !trialExpired;
      setIsActive(active);

      // Check if premium tier
      setIsPremium(['academic', 'scholar'].includes(sub.tier));

      // Calculate days left
      if (sub.is_trial && sub.trial_end) {
        const endDate = new Date(sub.trial_end);
        const diff = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        // null when expired so banner/guards treat it as no trial running
        setDaysLeft(diff >= 0 ? diff : null);
      } else if (sub.next_billing_date) {
        const endDate = new Date(sub.next_billing_date);
        const diff = Math.floor((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        setDaysLeft(diff > 0 ? diff : null);
      } else {
        setDaysLeft(null);
      }
    }
  }, [userProfile]);

  const subscription = userProfile?.subscription;

  // Complimentary users were backfilled with an active subscription but no
  // subscription_start_date (never billed). They have no real subscription to manage.
  const isComplimentary = !!subscription && !subscription.is_trial && !subscription.subscription_start_date;

  // Trial existed but the end date has passed and no paid subscription followed
  const isTrialExpired =
    !!subscription?.is_trial &&
    !!subscription?.trial_end &&
    new Date(subscription.trial_end) < new Date();

  return {
    subscription,
    isActive,
    isPremium,
    isTrial: subscription?.is_trial || false,
    isTrialExpired,
    isComplimentary,
    daysLeft,
    tier: subscription?.tier || 'specialist',
    courseLimit: subscription?.course_limit || 0,
  };
}
